import { getUserKey } from '@/utils/filtersStorage';
import {
  STICKY_NOTE_MIN_HEIGHT,
  STICKY_NOTE_WIDTH,
  STICKY_NOTES_STORAGE_PREFIX,
  type StickyNote,
  type StickyNoteColor,
} from './types';

export function getStickyNotesStorageKey(user: unknown): string {
  const userKey = getUserKey(user);
  return userKey ? `${STICKY_NOTES_STORAGE_PREFIX}:${userKey}` : STICKY_NOTES_STORAGE_PREFIX;
}

function isStickyNoteColor(value: unknown): value is StickyNoteColor {
  return (
    value === 'yellow' ||
    value === 'green' ||
    value === 'orange' ||
    value === 'red' ||
    value === 'blue' ||
    value === 'pink'
  );
}

function normalizeStickyNote(value: unknown): StickyNote | null {
  if (!value || typeof value !== 'object') return null;
  const note = value as Record<string, unknown>;
  if (
    typeof note.id !== 'string' ||
    !isStickyNoteColor(note.color) ||
    typeof note.text !== 'string' ||
    typeof note.x !== 'number' ||
    typeof note.y !== 'number' ||
    typeof note.zIndex !== 'number' ||
    typeof note.updatedAt !== 'string'
  ) {
    return null;
  }
  return {
    id: note.id,
    color: note.color,
    text: note.text,
    x: note.x,
    y: note.y,
    width: typeof note.width === 'number' ? note.width : STICKY_NOTE_WIDTH,
    height: typeof note.height === 'number' ? note.height : STICKY_NOTE_MIN_HEIGHT,
    zIndex: note.zIndex,
    minimized: typeof note.minimized === 'boolean' ? note.minimized : false,
    updatedAt: note.updatedAt,
  };
}

export function parseStickyNotes(raw: string | null): StickyNote[] {
  if (!raw) return [];
  try {
    const parsed = JSON.parse(raw);
    if (!Array.isArray(parsed)) return [];
    return parsed.map(normalizeStickyNote).filter((note): note is StickyNote => note !== null);
  } catch {
    return [];
  }
}

export function loadStickyNotes(user: unknown): StickyNote[] {
  try {
    const key = getStickyNotesStorageKey(user);
    return parseStickyNotes(localStorage.getItem(key));
  } catch (error) {
    console.error('Error loading sticky notes:', error);
    return [];
  }
}

export function saveStickyNotes(user: unknown, notes: StickyNote[]): void {
  try {
    const key = getStickyNotesStorageKey(user);
    localStorage.setItem(key, JSON.stringify(notes));
  } catch (error) {
    console.error('Error saving sticky notes:', error);
  }
}

export function createStickyNote(
  color: StickyNoteColor,
  options: { x: number; y: number; zIndex: number },
): StickyNote {
  return {
    id: `note-${Date.now()}-${Math.random().toString(36).slice(2, 9)}`,
    color,
    text: '',
    x: options.x,
    y: options.y,
    width: STICKY_NOTE_WIDTH,
    height: STICKY_NOTE_MIN_HEIGHT,
    zIndex: options.zIndex,
    minimized: false,
    updatedAt: new Date().toISOString(),
  };
}
